<template>
    <div class="register-form">
        <div class="register-form__title">Регистрация</div>

        <form class="register-form__fields" @submit.prevent="onSubmit">
            <UISelect
                id="dealer_sap_code"
                v-model="form.dealer_sap_code"
                :options="dealerCenterOptions"
                label="Дилерский центр*"
                :error-message="errors.dealer_sap_code"
                searchable
            />

            <UIInput
                id="email"
                v-model="form.email"
                label="Email*"
                :error-message="errors.email"
            />

            <UIPasswordInput
                id="password"
                v-model="form.password"
                label="Пароль*"
                :error-message="errors.password"
            />

            <UIPasswordInput
                id="password_confirmation"
                v-model="form.password_confirmation"
                label="Повторите пароль*"
                :error-message="errors.password_confirmation"
            />

            <UIInput
                id="last_name"
                v-model="form.last_name"
                label="Фамилия*"
                :error-message="errors.last_name"
                @input="validateLettersOnly('last_name')"
            />

            <UIInput
                id="first_name"
                v-model="form.first_name"
                label="Имя*"
                :error-message="errors.first_name"
                @input="validateLettersOnly('first_name')"
            />

            <UIInput
                id="second_name"
                v-model="form.second_name"
                label="Отчество"
                :error-message="errors.second_name"
                @input="validateLettersOnly('second_name')"
            />

            <UIInput
                id="position"
                v-model="form.position"
                label="Должность"
                :error-message="errors.position"
            />

            <UIPhoneInput
                id="phone"
                v-model="form.phone"
                label="Телефон*"
                :error-message="errors.phone"
            />

            <UICheckbox
                v-model="form.accept_auction_rules"
                :error-message="errors.accept_auction_rules"
            >
                Я согласен с
                <RulesLink>правилами проведения аукционов</RulesLink>
            </UICheckbox>

            <UICheckbox
                v-model="form.accept_rules_processing_personal_data"
                :error-message="errors.accept_rules_processing_personal_data"
            >
                Я даю своё согласие на
                <UILink :href="router.resolve({ name: 'ViewLegal' }).href" target="_blank">обработку персональных данных</UILink>
            </UICheckbox>

            <UISimpleButton type="submit" :loading="registerLoading">Зарегистрироваться</UISimpleButton>
        </form>
    </div>
</template>

<script lang="ts" setup>
    import { ref, onMounted } from 'vue';
    import { useRouter } from 'vue-router';

    import { injectStrict } from '@/injection/injectStrict.ts';
    import { AuthKey } from '@/injection/keys.ts';

    import { SelectOption } from '@/types/form.type.ts';
    import { DealerListItem } from '@/types/dealer.type.ts';

    import useDealerRepository from '@/repositories/dealer/useDealerRepository.ts';
    import useNotifications from '@/utils/useNotifications.ts';

    import UICheckbox from '@/components/ui/UICheckbox/UICheckbox.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UILink from '@/components/ui/UILink/UILink.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';
    import UIPasswordInput from '@/components/ui/UIPasswordInput/UIPasswordInput.vue';
    import UIPhoneInput from '@/components/ui/UIPhoneInput/UIPhoneInput.vue';
    import RulesLink from '@/components/shared/RulesLink/RulesLink.vue';

    const $Auth = injectStrict(AuthKey);

    const router = useRouter();
    const { getAllActiveDealers } = useDealerRepository();
    const { showSuccess, showWarning } = useNotifications();

    const form = ref({
        dealer_sap_code: undefined,
        email: '',
        password: '',
        password_confirmation: '',
        last_name: '',
        first_name: '',
        second_name: '',
        position: '',
        phone: '',
        accept_auction_rules: false,
        accept_rules_processing_personal_data: false,
    });
    const errors = ref<Record<string, string>>({});
    const registerLoading = ref<boolean>(false);
    const dealerCenterOptions = ref<SelectOption[]>([]);
    const tokenData = ref<any>(null);

    async function onSubmit(): Promise<void> {
        registerLoading.value = true;

        clearErrors();
        validateForm();

        if (Object.keys(errors.value).length > 0) {
            registerLoading.value = false;

            return;
        }

        try {
            if (!tokenData.value?.form_token_field_value) {
                await fetchToken();
                registerLoading.value = false;

                showWarning({
                    title: 'Требуется новый токен регистрации',
                    message: 'Вы сможете отправить форму повторно через несколько секунд',
                });

                return;
            }

            await $Auth.registerUser({
                email: form.value.email,
                dealer_sap_code: form.value.dealer_sap_code,
                last_name: form.value.last_name,
                first_name: form.value.first_name,
                second_name: form.value.second_name,
                position: form.value.position,
                phone: form.value.phone,
                password: form.value.password,
                password_confirmation: form.value.password_confirmation,
                form_token: tokenData.value.form_token_field_value,
                accept_auction_rules: form.value.accept_auction_rules,
                accept_rules_processing_personal_data: form.value.accept_rules_processing_personal_data,
            });

            showSuccess({
                title: 'Заявка на регистрацию отправлена',
                message: 'Доступ на портал будет открыт после одобрения администратором',
            });

            router.push({ name: 'Login' });
        }
        catch (error) {
            console.error('Ошибка регистрации:', error);
            handleErrors(error);
        }

        registerLoading.value = false;
    }

    function validateForm(): void {
        if (!form.value.dealer_sap_code) {
            errors.value.dealer_sap_code = 'Пожалуйста, выберите дилерский центр';
        }

        if (!form.value.email) {
            errors.value.email = 'Пожалуйста, введите email';
        }
        else if (!isValidEmail(form.value.email)) {
            errors.value.email = 'Пожалуйста, введите корректный email';
        }

        if (!form.value.password) {
            errors.value.password = 'Пожалуйста, введите пароль';
        }
        else if (form.value.password.length < 6) {
            errors.value.password = 'Пароль должен содержать не менее 6 символов';
        }

        if (form.value.password !== form.value.password_confirmation) {
            errors.value.password_confirmation = 'Пароли не совпадают';
        }

        if (!form.value.last_name) {
            errors.value.last_name = 'Пожалуйста, введите фамилию';
        }

        if (!form.value.first_name) {
            errors.value.first_name = 'Пожалуйста, введите имя';
        }
        // if (!form.value.second_name) {
        //     errors.value.second_name = 'Пожалуйста, введите отчество';
        // }
        // if (!form.value.position) {
        //     errors.value.position = 'Пожалуйста, введите должность';
        // }

        if (!form.value.phone || form.value.phone.length < 18) {
            errors.value.phone = 'Пожалуйста, введите корректный номер телефона';
        }

        if (!form.value.accept_auction_rules) {
            errors.value.accept_auction_rules = 'Необходимо согласие с правилами проведения аукционов';
        }

        if (!form.value.accept_rules_processing_personal_data) {
            errors.value.accept_rules_processing_personal_data = 'Необходимо согласие на обработку персональных данных';
        }
    }

    function validateLettersOnly(field: 'last_name' | 'first_name' | 'second_name'): void {
        const onlyLettersPattern = /^[а-яёa-z\s-]*$/iu;
        const value = form.value[field];

        if (!onlyLettersPattern.test(value)) {
            form.value[field] = value.replace(/[^а-яёa-z\s-]/giu, '');
        }
    }

    function isValidEmail(email: string): boolean {
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        return emailPattern.test(email);
    }

    async function fetchToken(): Promise<void> {
        try {
            tokenData.value = await $Auth.getRegisterToken();
        }
        catch (error) {
            console.error('Не удалось получить токен для регистрации:', error);
        }
    }

    function fetchDealers(): void {
        getAllActiveDealers().then((dealers: DealerListItem[]) => {
            const options: SelectOption[] = [];

            dealers.forEach((dealer: DealerListItem) => {
                options.push({
                    label: dealer.name + ' (' + dealer.sap_code + ')',
                    value: dealer.sap_code,
                });
            });

            dealerCenterOptions.value = options;
        });
    }

    function clearErrors(): void {
        errors.value = {};
    }

    function handleErrors(error: any): void {
        if (error.response && error.response.data && error.response.data.errors) {
            const errorData = error.response.data.errors;

            for (const key in errorData) {
                if (key === 'form_token' && !tokenData.value?.form_token_field_value) {
                    fetchToken();
                }

                if (Object.hasOwnProperty.call(errorData, key)) {
                    errors.value[key] = errorData[key].at(0);
                }
            }
        }
    }

    onMounted(async () => {
        await fetchToken();
        fetchDealers();
    });
</script>

<style lang="scss" scoped>
    @use './RegisterForm.scss' as *;
</style>
